<template>
  <div class="mt-2">
    <div class="container-fluid">
      <div class="row d-flex justify-content-start">
        <div class="col-12 px-0 pt-2 pb-0">
          <SectionTitle
            class="responsive-section-title"
            icon="fas fa-file-medical"
            has-line
            content="Documentos Añadidos"
            bg-icon="green"
          />
        </div>
      </div>

      <div
        v-if="shouldAddDocuments && !model.documents.length"
        class="row justify-content-center align-items-center box-container"
      >
        <div class="col-12 col-md-6">
          <BoxIconButton
            :icon="'fas fa-file-plus'"
            :title="'Presione aquí para anejar documento'"
            :disabled="readOnly"
            @click="toggleShouldAddDocuments()"
          />
        </div>
      </div>
      <div v-else-if="!readOnly">
        <div v-if="shouldAddDocuments" class="row">
          <div class="col-12 d-flex justify-content-end align-items-end py-3">
            <ActionButton
              variant="success"
              i="fas fa-file-plus"
              :disabled="readOnly"
              @click="toggleShouldAddDocuments()"
            >
              Añadir
            </ActionButton>
          </div>
        </div>
        <ValidationObserver v-else ref="documentsForm">
          <div class="row mb-5 form-container">
            <div class="col-12 col-md-4 pt-0 pt-md-3">
              <ValidationProvider v-slot="{ errors }" rules="required">
                <custom-drop-down
                  v-model="value.documentType"
                  :options="documentTypeOptions"
                  fieldkey="id"
                  fieldname="name"
                  label="Tipo de Documento:"
                  placeholder="Seleccione"
                  :error="errors[0]"
                  :custom-error-msg="resources['9092']"
                  :disabled="readOnly"
                />
              </ValidationProvider>
            </div>
            <div class="col-12 col-md-5 pt-3">
              <span class="custom-file-input-label">Archivo:</span>
              <ValidationProvider v-slot="{ errors }" rules="required">
                <CustomFileInput
                  ref="fileUpload"
                  v-model="value.documentFile"
                  :on-download="() => {}"
                  :accept="'.pdf'"
                  label-position="left"
                  :error="errors[0]"
                  :custom-error-msg="resources['25024']"
                  :disabled="readOnly"
                />
              </ValidationProvider>
            </div>
            <div
              class="col-12 col-md-3 d-flex justify-content-center align-items-center pt-3"
            >
              <div class="row max-width-sm">
                <div class="col-6 text-right">
                  <ActionButton
                    variant="outline-danger"
                    i="fas fa-times-circle"
                    :disabled="readOnly"
                    text="Cancelar"
                    @click="toggleShouldAddDocuments()"
                  />
                </div>
                <div class="col-6 pl-0 pl-md-4">
                  <ActionButton
                    variant="success"
                    i="fas fa-file-plus"
                    :disabled="readOnly"
                    text="Añadir"
                    @click="onAddDocument()"
                  />
                </div>
              </div>
            </div>
          </div>
        </ValidationObserver>
      </div>
      <div v-if="model.documents.length">
        <div
          v-for="item in model.documents"
          :key="item.documentId"
          class="row bg-gray mb-3 py-4"
        >
          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Tipo de Documento:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.type }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-6 col-md-5">
            <div class="row">
              <div class="col-12">
                <span class="font-weight-normal">Archivo:</span>
              </div>
              <div class="col-12">
                <span class="font-weight-regular">
                  {{ item.fileName }}
                </span>
              </div>
            </div>
          </div>

          <div class="col-12 d-block d-md-none py-2" />

          <div class="col-12 col-md-2 text-center">
            <drop-down :items="getActions(item)" class="d-sm-block" />
          </div>
        </div>
      </div>
      <div
        v-if="!value.isDocumentTypeValid || !value.isDocumentComplaintValid"
        class="row mt-2"
      >
        <div class="col">
          <Error
            v-if="!value.isDocumentTypeValid"
            variant="no-border"
            :message="resources['25025']"
          />
          <Error
            v-if="!value.isDocumentComplaintValid"
            variant="no-border"
            :message="resources['25026']"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SectionTitle from 'SectionTitle';
import DropDown from 'dropdown';
import mixAlert from '@/mixin/alert.mixin';
import resourcesService from '@/services/resources.js';
import BoxIconButton from '@/components/pei/shared/BoxIconButton';
import CustomDropDown from 'custom-drop-down';
import ActionButton from 'ActionButton';
import CustomFileInput from 'CustomFileInput';
import { getATDocumentTypes } from '@/modules/request-tech-service-assistance/api';
import DocumentTypes from '@/utils/constants/documentTypes';
import Error from 'custom-error';

export default {
  name: 'DocumentsInformation',
  mixins: [mixAlert],
  components: {
    SectionTitle,
    DropDown,
    BoxIconButton,
    CustomDropDown,
    ActionButton,
    CustomFileInput,
    Error,
  },
  data() {
    return {
      shouldAddDocuments: true,
      documentTypes: [],
      complaintCopyOption: {},
    };
  },
  props: {
    value: {
      type: Object,
      required: true,
    },
    readOnly: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    model() {
      return this.value;
    },
    documentTypeOptions() {
      const result = this.documentTypes.filter(
        (option) =>
          option.id !==
          this.model.documents.find(
            (e) => e?.id === option.id && e?.id !== DocumentTypes.Otro
          )?.id
      );
      if (
        this.value.equipmentsToReplaceForTheft.length &&
        !this.model.documents.some((e) => e.id === this.complaintCopyOption.id)
      ) {
        result.push(this.complaintCopyOption);
      }
      return result;
    },
  },
  methods: {
    getActions(item) {
      let actions = [
        {
          label: 'Descargar',
          icon: 'fas fa-file-download text-green pr-1',
          click: () => this.downloadFile(item),
        },
      ];
      if (!this.readOnly) {
        actions.push({
          label: 'Eliminar',
          icon: 'fas fa-times-circle text-red pr-1',
          click: () => this.confirmDeleteFile(item),
        });
      }
      return actions;
    },
    async onAddDocument() {
      const isValid = await this.$refs.documentsForm.validate();

      if (!isValid) {
        return;
      }

      const document = this.value.documentType;
      const file = {
        id: document.id,
        type: document.name,
        fileName: this.value.documentFile.name,
        file: this.value.documentFile,
      };
      this.value.documents.push(file);
      this.$refs.fileUpload.removeFile();
      this.value.documentType = null;
      this.value.documentFile = null;
      this.$refs.documentsForm.reset();
      this.toggleShouldAddDocuments();
    },
    toggleShouldAddDocuments() {
      this.shouldAddDocuments = !this.shouldAddDocuments;
    },
    downloadFile(item) {
      let blob;

      if (item.id) {
        let binaryString = window.atob(item.file);
        let binaryLen = binaryString.length;
        let bytes = new Uint8Array(binaryLen);
        for (let i = 0; i < binaryLen; i++) {
          let ascii = binaryString.charCodeAt(i);
          bytes[i] = ascii;
        }
        blob = new Blob([bytes], { type: 'application/pdf' });
      } else {
        let fr = new FileReader();
        fr.readAsDataURL(item.file);
        blob = new Blob([item.file], { type: 'application/pdf' });
      }

      let objectURL = window.URL.createObjectURL(blob);

      let link = document.createElement('a');
      link.href = objectURL;
      link.download = item.file.name ?? item.fileName;
      document.body.appendChild(link);
      link.click();
      link.remove();
    },
    async confirmDeleteFile(item) {
      const answer = await this.ShowCancelModal(
        'Confirmación',
        '¿Está seguro que desea eliminar el documento?'
      );
      if (answer) {
        this.model.documents = this.model.documents.filter(
          (e) => e.id !== item.id
        );
      }
    },
    getError(code) {
      let message = this.resources[code];

      return message ? message.replace(`(${code})`, '') : '';
    },
    async loadResources() {
      this.resources = await resourcesService.LoadValidationMessages([
        '9092',
        '25024',
        '25025',
        '25026',
      ]);
    },
  },
  async mounted() {
    const { data } = await getATDocumentTypes();
    this.complaintCopyOption = data.find(
      (o) => o.id === DocumentTypes.CopiaDeQuerella
    );
    this.documentTypes = data.filter(
      (e) =>
        e.id !== DocumentTypes.PreviousEvaluationAT &&
        e.id !== DocumentTypes.CopiaDeQuerella
    );
    this.loadResources();
  },
};
</script>

<style scoped>
::v-deep .ac-btn:not(:hover) {
  background: transparent !important;
}

.box-container {
  background-color: #f7f7f7;
  padding: 15px;
  border-radius: 5px;
}

.form-container {
  min-height: 110px;
}

.custom-file-input-label {
  display: block;
  color: #727272;
  margin-bottom: 8px;
  font-size: 15px;
}

.max-width-sm {
  width: 100%;
}
.max-width-sm > .col-6 {
  padding-left: 4px;
  padding-right: 4px;
}

@media (max-width: 765px) {
  .d-sm-block >>> button,
  .d-sm-block >>> .dropdown-menu.show {
    width: 100%;
  }
  .responsive-section-title >>> .tag-badge-title-info {
    font-size: 0.8rem;
  }
  span.font-weight-normal {
    font-size: 12px !important;
  }
  .custom-file-input-label {
    font-size: 12px;
  }
  .custom-ddl >>> label.data-name {
    font-size: 12px !important;
  }
  ::v-deep .file-input-container > label.file-select > span.font-weight-bold {
    font-size: 14px;
  }
  ::v-deep .file-input-container > label.file-select > em {
    padding-right: 5px !important;
  }
  ::v-deep .file-input-container {
    padding-top: 14px;
    padding-bottom: 14px;
  }
  .box-container {
    padding-left: 0px;
    padding-right: 0px;
  }
}
</style>
